import {
    ApiErrorResponse,
    ApiResponse,
    ApiSuccessResponse,
} from '~/util/types/api';
import { rootNavigate } from './NavigatorService';

const FALLBACK_BASE_URL = 'https://api.aredl.net/v2dev/api';

// API CONTEXT (to apply auth and staff headers)

type ApiRequestContext = {
    token?: string;
    hasStaffScope?: boolean;
};

export function setApiRequestContext(context: ApiRequestContext | null) {
    apiRequestContext = context ?? {};
}

export function getApiRequestContext() {
    return apiRequestContext;
}

let apiRequestContext: ApiRequestContext = {};

// CORE API REQUESTS

export class ApiError extends Error {
    status: number;
    message: string;
    data: unknown;
    _networkError: boolean;

    constructor(
        message: string,
        status: number,
        data: unknown,
        networkError = false,
    ) {
        super(message);
        this.message = message;
        this.status = status;
        this.data = data;
        this._networkError = networkError;
    }
}

export interface ApiRequest {
    path: string;
    method?: string;
    query?: object;
    body?: object;
    token?: string;
    headers?: Record<string, string>;
    attachAuth?: boolean;
}

export interface ApiRequestWithToast<T> {
    requestData: ApiRequest;
    loadingMessage?: string;
    errorMessage?: string | ((response: ApiErrorResponse) => string);
    successMessage?: string | ((response: ApiSuccessResponse<T>) => string);
    errorCallback?: (error: ApiErrorResponse) => void;
    successCallback?: (response: ApiSuccessResponse<T>) => void;
}

export interface CoreApi {
    baseUrl: string;
    send: <T = unknown>(data: ApiRequest) => Promise<ApiResponse<T>>;
}

export interface Api extends CoreApi {
    sendWithToast: <T = unknown>(
        data: ApiRequestWithToast<T>,
    ) => Promise<ApiResponse<T>>;
}

async function readResponseBody<T>(
    response: Response,
): Promise<T | { message: string }> {
    const contentLength = response.headers.get('Content-Length');
    if (contentLength && parseInt(contentLength, 10) == 0) {
        return { message: 'Empty response.' };
    }

    const contentType = response.headers.get('Content-Type');
    return contentType?.includes('application/json')
        ? await response.json()
        : { message: await response.text() };
}

export const api: CoreApi = {
    baseUrl: import.meta.env.VITE_BASE_API_URL ?? FALLBACK_BASE_URL,
    send: async function <T = unknown>(
        data: ApiRequest,
    ): Promise<ApiResponse<T>> {
        const { token, hasStaffScope } = getApiRequestContext();

        let cleanedQuery: [string, string][] | undefined;
        if (data.query) {
            cleanedQuery = Object.entries(data.query)
                .filter((elt) => elt[1] !== null && elt[1] !== undefined)
                .map(([key, value]) => [key, String(value)]);
        }

        const url =
            this.baseUrl +
            data.path +
            (data.query ? '?' + new URLSearchParams(cleanedQuery) : '');

        const method = (data.method ?? 'GET').toUpperCase();
        const headers = data.headers ?? {};
        let body: string | undefined;

        if (data.token || (data.attachAuth && token)) {
            headers['Authorization'] = `Bearer ${data.token ?? token}`;
        }

        if (hasStaffScope) {
            headers['staff-portal'] = 'true';
        }

        if (data.body != null && method !== 'GET' && method !== 'HEAD') {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(data.body);
        }

        const cache = hasStaffScope ? 'no-store' : undefined;

        try {
            const response = await fetch(url, { method, headers, body, cache });

            if (!response.ok) {
                const responseData = (await readResponseBody(response)) as {
                    message: string;
                };

                if (
                    response.status === 403 &&
                    responseData.message === 'Token has been invalidated'
                ) {
                    console.error('Token has been invalidated');
                    rootNavigate({
                        to: '/logout',
                        search: { showToast: 'tokenInvalidated' },
                    });
                }

                if (response.status === 429) {
                    console.error('Token has been invalidated');
                    return {
                        status: response.status,
                        data: {
                            message:
                                'You are being rate limited. Please try again later.',
                        },
                        error: true,
                        networkError: false,
                    };
                }

                return {
                    status: response.status,
                    data: responseData,
                    error: true,
                    networkError: false,
                };
            }

            const responseData = await readResponseBody<T>(response);

            return {
                status: response.status,
                data: responseData,
                error: false,
                networkError: false,
            } as ApiResponse<T>;
        } catch (error) {
            console.error(`Failed to fetch:`, error);
            return {
                status: 500,
                data: {
                    message: 'Failed to fetch data from the server.',
                },
                error: true,
                networkError: true,
            };
        }
    },
};
